import './App.css';
import React, { useCallback, useEffect, useState } from 'react';
import fernet from 'fernet';
import { Buffer } from 'buffer/';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from './components/Loader';
import { AlertIcon, ChatIcon, MailIcon } from './components/Icons';
import { apiErrorMessage } from './utils/format';
import { setPartner, useTrackError } from './utils/analytics';

export const BASE_URL = 'https://api.getfleek.app';
// export const BASE_URL = 'http://127.0.0.1:8000';

const cx = (...parts) => parts.filter(Boolean).join(' ');

/** Partner slug used when getfleek.com is hit at the root. */
const DEFAULT_ROOT_SLUG = 'visa';

/**
 * Legacy: the slug, Fernet-encrypted with a key that ships in this bundle.
 * That makes it obfuscation, not security: the slug is in the link anyway and
 * the API's only real check is that it names an active partner. The API now
 * takes the plain `client_id`; this is sent alongside it only so the portal
 * keeps working against an API that predates that. Remove it (and the fernet
 * and buffer dependencies) once the API accepting `client_id` is live.
 */
const encryptKeyword = (keyword) => {
  const rawKey = 'PARTNER_PORTAL_ENCRYPTION_KEY2025';

  // Pad or truncate the raw key to exactly 32 bytes
  const paddedKey = Buffer.alloc(32);
  Buffer.from(rawKey).copy(paddedKey);

  // url-safe base64
  const b64Key = paddedKey
    .toString('base64')
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');

  const secret = new fernet.Secret(b64Key);

  const iv = [];
  for (let i = 0; i < 16; i++) {
    iv.push(Math.floor(Math.random() * 256));
  }

  const token = new fernet.Token({ secret, time: Date.now(), iv });
  return token.encode(keyword);
};

/**
 * Resolves the partner slug for this visit and exchanges it for a portal
 * token, then hands off to the marketplace.
 */
const GetPortalToken = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [error, setError] = useState(null);
  useTrackError('portal_token', error);

  const resolveSlug = useCallback(() => {
    // An explicit /:partnerSlug in the URL always wins.
    if (params.partnerSlug) return params.partnerSlug;

    const hostname = window.location.hostname;
    const isRootDomain =
      hostname === 'getfleek.com' ||
      hostname === 'www.getfleek.com' ||
      hostname === 'visa.getfleek.com' ||
      hostname === 'www.visa.getfleek.com';

    if (isRootDomain) return DEFAULT_ROOT_SLUG;

    // A slug we've already used in this browser (e.g. returning after a
    // session expiry on a custom partner domain).
    return localStorage.getItem('partnerSlug') || null;
  }, [params.partnerSlug]);

  const bootstrap = useCallback(async () => {
    setError(null);

    const slug = resolveSlug();

    if (!slug) {
      setError(
        "We couldn't work out which partner marketplace to open. Please use the link your provider sent you."
      );
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/partner/v2/get-portal-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: slug,
          encrypted_client_id: encryptKeyword(slug),
        }),
        redirect: 'follow',
      });

      const result = await response.json().catch(() => ({}));

      // A missing token used to leave the spinner running forever with no
      // message and nothing to click.
      if (!response.ok || !result.token) {
        // The API answers an unknown slug with "Invalid credentials", which
        // means nothing to a member who simply followed a bad link. Log the
        // real reason, show them something they can act on.
        console.error(
          'Portal token rejected:',
          apiErrorMessage(result, `HTTP ${response.status}`)
        );
        setError(
          "We couldn't open this marketplace. Double-check the link your provider sent you, or get in touch and we'll sort it out."
        );
        return;
      }

      // Only now is the slug known-good. Writing it before the exchange meant
      // one visit to a bad link left that slug cached, so every later visit
      // re-tried it and failed.
      localStorage.setItem('partnerSlug', slug);
      localStorage.setItem('portalToken', result.token);
      setPartner(slug);

      const returnTo = sessionStorage.getItem('returnTo');
      if (returnTo) {
        sessionStorage.removeItem('returnTo');
        navigate(returnTo, { replace: true });
      } else {
        navigate('/marketplace', { replace: true });
      }
    } catch (err) {
      console.error('Portal token request failed:', err);
      setError(
        'We could not reach Fleek. Please check your connection and try again.'
      );
    }
  }, [navigate, resolveSlug]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  if (error) {
    return (
      <div className="page" style={{ paddingTop: 0 }}>
        <div className="statusWrap">
          <div className="statusCard">
            <div className="statusIcon statusIconBad" aria-hidden="true">
              <AlertIcon width="28" height="28" />
            </div>
            <h1 className="statusTitle">Marketplace unavailable</h1>
            <p className="statusBody">{error}</p>
            <div className="statusActions">
              <button
                type="button"
                className={cx('btn', 'btnMd', 'btnBrand')}
                onClick={bootstrap}
              >
                Try again
              </button>
            </div>
            <div className="supportRow" style={{ justifyContent: 'center' }}>
              <a
                className={cx('btn', 'btnSm', 'btnSecondary')}
                href="https://wa.me/919148477518"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ChatIcon />
                WhatsApp
              </a>
              <a
                className={cx('btn', 'btnSm', 'btnSecondary')}
                href="mailto:help@getfleek.app"
              >
                <MailIcon />
                Email
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ paddingTop: 0 }}>
      <div className="statusWrap">
        <Loader />
        <p className="statusBody">Opening your marketplace…</p>
      </div>
    </div>
  );
};

function App() {
  return <GetPortalToken />;
}

export default App;
